import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material'
import {
  Add as AddIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  Science as ExperimentIcon,
  ContentCopy as DuplicateIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material'
import { useParams, useNavigate } from 'react-router-dom'
import { agentPromptsAPI } from '../services/api'
import { useAuth } from '../contexts/AuthContext'

const AGENT_TYPES = [
  { value: 'assessment', label: 'Assessment', description: 'Conducts clinical assessments and questionnaires' },
  { value: 'skill_building', label: 'Skill Building', description: 'Teaches coping skills and techniques' },
  { value: 'education', label: 'Education', description: 'Delivers psychoeducational content' },
  { value: 'general_support', label: 'General Support', description: 'Open-ended supportive conversations' },
]

const TONE_OPTIONS = ['warm', 'gentle', 'encouraging', 'direct', 'professional', 'conversational']
const APPROACH_OPTIONS = ['socratic', 'story_based', 'instructive', 'supportive', 'guided']

function AgentPrompts() {
  const { agentType } = useParams()
  const navigate = useNavigate()
  const { canManageBaseContent } = useAuth()
  const [prompts, setPrompts] = useState({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedType, setSelectedType] = useState(agentType || null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingPrompt, setEditingPrompt] = useState(null)
  const [saving, setSaving] = useState(false)
  const [formData, setFormData] = useState({
    persona: '',
    base_instructions: '',
    default_tone: 'warm',
    default_approach: 'supportive',
    response_format: '',
    tool_instructions: '',
    experiment_id: '',
    variant: 'A',
    weight: 100,
  })

  useEffect(() => {
    fetchPrompts()
  }, [])

  useEffect(() => {
    if (agentType) {
      setSelectedType(agentType)
    }
  }, [agentType])

  const fetchPrompts = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await agentPromptsAPI.getAll()
      // Group by agent_type
      const grouped = (response.data || []).reduce((acc, prompt) => {
        if (!acc[prompt.agent_type]) acc[prompt.agent_type] = []
        acc[prompt.agent_type].push(prompt)
        return acc
      }, {})
      setPrompts(grouped)
    } catch (err) {
      console.error('Error fetching agent prompts:', err)
      setError('Failed to load agent prompts. The API endpoint may not be configured yet.')
      setPrompts({})
    } finally {
      setLoading(false)
    }
  }

  // Helper to convert object fields to strings for editing
  const formatFieldForEdit = (value) => {
    if (value === null || value === undefined) return ''
    if (typeof value === 'object') {
      return JSON.stringify(value, null, 2)
    }
    return value
  }

  const handleOpenDialog = (prompt = null, type = null) => {
    if (prompt) {
      setEditingPrompt(prompt)
      setFormData({
        persona: prompt.persona || '',
        base_instructions: prompt.base_instructions || '',
        default_tone: prompt.default_tone || 'warm',
        default_approach: prompt.default_approach || 'supportive',
        response_format: formatFieldForEdit(prompt.response_format),
        tool_instructions: prompt.tool_instructions || '',
        experiment_id: prompt.experiment_id || '',
        variant: prompt.variant || 'A',
        weight: prompt.weight || 100,
      })
    } else {
      setEditingPrompt({ agent_type: type || selectedType })
      setFormData({
        persona: '',
        base_instructions: '',
        default_tone: 'warm',
        default_approach: 'supportive',
        response_format: '',
        tool_instructions: '',
        experiment_id: '',
        variant: 'A',
        weight: 100,
      })
    }
    setDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setDialogOpen(false)
    setEditingPrompt(null)
  }

  // Helper to parse JSON string fields back to objects for saving
  const parseFieldForSave = (value) => {
    if (!value || typeof value !== 'string') return value
    const trimmed = value.trim()
    if ((trimmed.startsWith('{') && trimmed.endsWith('}')) ||
        (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
      try {
        return JSON.parse(trimmed)
      } catch {
        return value // Keep as string if invalid JSON
      }
    }
    return value
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      setError(null)
      const dataToSave = {
        ...formData,
        agent_type: editingPrompt.agent_type,
        response_format: parseFieldForSave(formData.response_format),
      }
      if (editingPrompt.id) {
        await agentPromptsAPI.update(editingPrompt.id, dataToSave)
      } else {
        await agentPromptsAPI.create(dataToSave)
      }
      handleCloseDialog()
      fetchPrompts()
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save agent prompt.')
    } finally {
      setSaving(false)
    }
  }

  const handleDuplicate = async (prompt) => {
    try {
      setError(null)
      await agentPromptsAPI.duplicate(prompt.id, {
        variant: `${prompt.variant || 'A'}_copy`,
      })
      fetchPrompts()
    } catch (err) {
      console.error('Error duplicating agent prompt:', err)
      setError(err.response?.data?.message || 'Failed to duplicate agent prompt.')
    }
  }

  // Check if user can edit a specific prompt
  const canEditPrompt = (prompt) => {
    if (prompt.is_base && !canManageBaseContent('agent_prompts')) return false
    return true
  }

  const getVariantCount = (type) => {
    return prompts[type]?.length || 0
  }

  const getTypeInfo = (type) => {
    return AGENT_TYPES.find((t) => t.value === type) || { label: type, description: '' }
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
        <Box>
          <Typography variant="h5">Agent Prompts</Typography>
          <Typography variant="body2" color="text.secondary">
            Configure base prompts for each agent type. Use variants for A/B testing.
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Agent Type Cards */}
      <Grid container spacing={2} mb={2}>
        {AGENT_TYPES.map((type) => (
          <Grid item xs={12} sm={6} md={4} key={type.value}>
            <Paper
              sx={{
                p: 2,
                cursor: 'pointer',
                borderWidth: selectedType === type.value ? 2 : 1,
                borderColor: selectedType === type.value ? 'primary.main' : 'divider',
                '&:hover': { borderColor: 'primary.light' },
              }}
              onClick={() => {
                setSelectedType(type.value)
                navigate(`/agent-prompts/${type.value}`)
              }}
            >
              <Typography variant="subtitle1" fontWeight={500} gutterBottom>
                {type.label}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {type.description}
              </Typography>
              <Typography variant="caption" color={getVariantCount(type.value) > 0 ? 'primary.main' : 'text.secondary'}>
                {getVariantCount(type.value)} variant{getVariantCount(type.value) !== 1 ? 's' : ''}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Selected Agent Type Details */}
      {selectedType && (
        <Paper sx={{ p: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box>
              <Typography variant="subtitle1" fontWeight={500}>
                {getTypeInfo(selectedType).label} Agent
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {getTypeInfo(selectedType).description}
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog(null, selectedType)}
            >
              Add Variant
            </Button>
          </Box>

          <Divider sx={{ my: 2 }} />

          {(prompts[selectedType] || []).length === 0 ? (
            <Alert severity="info">
              No prompts configured for this agent type. Add a variant to get started.
            </Alert>
          ) : (
            (prompts[selectedType] || []).map((prompt, index) => (
              <Accordion key={prompt.id} defaultExpanded={index === 0}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%', pr: 2 }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body2" fontWeight={500}>
                        Variant {prompt.variant || String.fromCharCode(65 + index)}
                      </Typography>
                      {prompt.experiment_id && (
                        <Typography variant="caption" color="text.secondary">
                          <ExperimentIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
                          {prompt.experiment_id}
                        </Typography>
                      )}
                      {prompt.is_base && (
                        <Typography variant="caption" color="text.secondary">
                          Base
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ flexGrow: 1 }} />
                    <Typography variant="caption" color="primary.main">
                      {prompt.weight || 100}% weight
                    </Typography>
                    <Tooltip title="Duplicate">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleDuplicate(prompt)
                        }}
                      >
                        <DuplicateIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={canEditPrompt(prompt) ? 'Edit' : 'View'}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleOpenDialog(prompt)
                        }}
                      >
                        {canEditPrompt(prompt) ? (
                          <EditIcon fontSize="small" />
                        ) : (
                          <ViewIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" gutterBottom>
                          Persona
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                          {prompt.persona || 'Not defined'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Typography variant="subtitle2" gutterBottom>
                          Default Tone
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                          {prompt.default_tone || 'warm'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Typography variant="subtitle2" gutterBottom>
                          Default Approach
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                          {(prompt.default_approach || 'supportive').replace(/_/g, ' ')}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" gutterBottom>
                          Base Instructions
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            whiteSpace: 'pre-wrap',
                            maxHeight: 200,
                            overflow: 'auto',
                            bgcolor: 'grey.50',
                            p: 1,
                            borderRadius: 1,
                            fontFamily: 'monospace',
                            fontSize: '0.8rem',
                          }}
                        >
                          {prompt.base_instructions || 'Not defined'}
                        </Typography>
                      </Grid>
                      {prompt.experiment_id && (
                        <Grid item xs={12}>
                          <Alert severity="info" variant="outlined">
                            This variant is part of experiment "{prompt.experiment_id}" with {prompt.weight}% traffic allocation.
                          </Alert>
                        </Grid>
                      )}
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              ))
            )}
        </Paper>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingPrompt?.id ? 'Edit Prompt Variant' : 'Create Prompt Variant'}
          {editingPrompt?.agent_type && (
            <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 2 }}>
              ({getTypeInfo(editingPrompt.agent_type).label})
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Persona"
                value={formData.persona}
                onChange={(e) => setFormData({ ...formData, persona: e.target.value })}
                multiline
                rows={3}
                placeholder="Describe the agent's personality and role..."
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Default Tone</InputLabel>
                <Select
                  value={formData.default_tone}
                  label="Default Tone"
                  onChange={(e) => setFormData({ ...formData, default_tone: e.target.value })}
                >
                  {TONE_OPTIONS.map((tone) => (
                    <MenuItem key={tone} value={tone}>
                      {tone.charAt(0).toUpperCase() + tone.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Default Approach</InputLabel>
                <Select
                  value={formData.default_approach}
                  label="Default Approach"
                  onChange={(e) => setFormData({ ...formData, default_approach: e.target.value })}
                >
                  {APPROACH_OPTIONS.map((approach) => (
                    <MenuItem key={approach} value={approach}>
                      {approach.replace(/_/g, ' ').charAt(0).toUpperCase() + approach.replace(/_/g, ' ').slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Base Instructions"
                value={formData.base_instructions}
                onChange={(e) => setFormData({ ...formData, base_instructions: e.target.value })}
                multiline
                rows={8}
                placeholder="Core instructions for this agent type..."
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Response Format Guidelines"
                value={formData.response_format}
                onChange={(e) => setFormData({ ...formData, response_format: e.target.value })}
                multiline
                rows={3}
                placeholder="Guidelines for response structure and length..."
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Tool Instructions"
                value={formData.tool_instructions}
                onChange={(e) => setFormData({ ...formData, tool_instructions: e.target.value })}
                multiline
                rows={3}
                placeholder="Instructions for using available tools..."
              />
            </Grid>
            <Grid item xs={12}>
              <Divider>
                <Typography variant="caption" color="text.secondary">
                  A/B Testing (Optional)
                </Typography>
              </Divider>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Experiment ID"
                value={formData.experiment_id}
                onChange={(e) => setFormData({ ...formData, experiment_id: e.target.value })}
                placeholder="e.g., EDU_TONE_TEST"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Variant Name"
                value={formData.variant}
                onChange={(e) => setFormData({ ...formData, variant: e.target.value })}
                placeholder="e.g., A, B, Control"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Weight (%)"
                type="number"
                value={formData.weight}
                onChange={(e) => setFormData({ ...formData, weight: parseInt(e.target.value) || 100 })}
                inputProps={{ min: 0, max: 100 }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>
            {saving ? 'Saving...' : editingPrompt?.id ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default AgentPrompts
